import { Component } from 'preact';
import RuneSpinner from './RuneSpinner';
import { fetchConversations } from '../utils/arena/chatApi';
import { getCachedConversations, saveCachedConversations } from '../utils/arena/chatCache';
import { resolveAvatarUrl } from '../utils/arena/avatarUtils';
import AvatarImage from './ui/AvatarImage';
import RowArtBackdrop from './RowArtBackdrop';
import { cardMentionsToPlainText } from '../utils/cardMentions';
import PlayerTitle from './PlayerTitle';
import {
  searchPlayers, sendFriendRequest, acceptFriendRequest, declineFriendRequest,
} from '../utils/friendsApi';
import { refreshFriendList } from '../utils/presenceManager';
import { onServerReconnect } from '../utils/serverClient';
import {
  GOLD, TEXT_PRIMARY, TEXT_BODY, TEXT_MUTED, ACCENT_GOLD,
  GOLD_BTN, BEVELED_BTN, OPAQUE_SUB_PANEL_STYLE,
} from '../lib/medievalTheme';
import { t } from '../localization/t';

const PRESENCE_DOT = {
  online: '#6ac084',
  idle: '#e0a060',
  dnd: '#c45050',
};

// Mirrors Mailbox's ladder: this list renders inside that panel's
// 0.7cqw padding, so its rows and fields round at 0.5cqw and pad 0.625cqw.
const ROW_RADIUS = 'var(--radius-lg)';
const ROW_PADDING_X = '0.625cqw';
const AVATAR_SIZE = '2.25cqw';
const AVATAR_RING = 'inset 0 0 0 1px rgba(255,255,255,0.08)';
// Solid stand-in for the panel surface, for the cutout ring the presence
// dot punches out of the avatar edge.
const PANEL_RING = '#151107';

const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
  if (seconds < 60) return t('now');
  if (seconds < 3600) return t('{n}m', { n: Math.floor(seconds / 60) });
  if (seconds < 86400) return t('{n}h', { n: Math.floor(seconds / 3600) });
  return t('{n}d', { n: Math.floor(seconds / 86400) });
}

function truncate(text, len = 40) {
  if (!text) return '';
  return text.length > len ? text.slice(0, len) + '...' : text;
}

export default class ChatConversationList extends Component {
  constructor(props) {
    super(props);
    const cached = getCachedConversations(props.myUserId);
    this.state = {
      conversations: Array.isArray(cached) ? cached : [],
      loading: !Array.isArray(cached),
      searchQuery: '',
      serverResults: null,
      searchLoading: false,
      sentRequests: new Set(),
      autoAccepted: new Set(),
      // senderIds of requests being accepted/declined right now, and
      // senderIds of requests that have been handled locally. Both
      // hide the row until the authoritative refresh replaces
      // pendingRequests. Prevents double-fires and removes the
      // "still visible but already 404s" flicker.
      actingOn: new Set(),
      handled: new Set(),
      // Conversation friend IDs whose crown title resolved. Drives the
      // trailing comma on the friend name in each conversation row.
      conversationIdsWithTitle: new Set(),
    };
  }

  componentDidMount() {
    this.loadConversations();
    // Safety net for invites that arrive while the WS is down or the
    // window is backgrounded. The `chat:message` push is fire-and-
    // forget on the server (no replay queue), so a recipient who was
    // offline/backgrounded when the invite landed would otherwise see
    // stale conversation state until they switch tabs or restart.
    // These listeners guarantee the list catches up on reconnect and
    // on window refocus.
    this._reconnectUnsub = onServerReconnect(() => this.loadConversations());
    this._onVisibilityChange = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
        this.loadConversations();
      }
    };
    this._onFocus = () => this.loadConversations();
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this._onVisibilityChange);
    }
    if (typeof window !== 'undefined') {
      window.addEventListener('focus', this._onFocus);
    }
  }

  componentDidUpdate(prevProps) {
    // Prune `handled` entries for senderIds the server no longer
    // reports as pending. Once the authoritative list catches up
    // we don't need to keep hiding the row, and we don't want to
    // hide a NEW request later from the same sender.
    const prev = prevProps.friendListData?.pendingRequests;
    const curr = this.props.friendListData?.pendingRequests;
    if (prev !== curr && this.state.handled.size > 0) {
      const currentIds = new Set((curr || []).map((r) => r.senderId || r.id));
      const pruned = new Set();
      for (const id of this.state.handled) {
        if (currentIds.has(id)) pruned.add(id);
      }
      if (pruned.size !== this.state.handled.size) this.setState({ handled: pruned });
    }
  }

  componentWillUnmount() {
    clearTimeout(this.searchTimeout);
    this._reconnectUnsub?.();
    if (typeof document !== 'undefined' && this._onVisibilityChange) {
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
    }
    if (typeof window !== 'undefined' && this._onFocus) {
      window.removeEventListener('focus', this._onFocus);
    }
  }

  loadConversations = async () => {
    try {
      const conversations = await fetchConversations();
      const list = Array.isArray(conversations) ? conversations : [];
      saveCachedConversations(this.props.myUserId, list);
      this.setState({ conversations: list, loading: false });
    } catch (err) {
      console.error('[ChatConversationList] Failed to load:', err);
      this.setState((s) => ({ loading: false, conversations: s.conversations }));
    }
  };

  refresh = () => this.loadConversations();

  handleSearchInput = (e) => {
    const searchQuery = e.target.value;
    this.setState({ searchQuery });

    clearTimeout(this.searchTimeout);
    const trimmed = searchQuery.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      this.setState({ serverResults: null, searchLoading: false });
      return;
    }

    this.setState({ searchLoading: true });
    this.searchTimeout = setTimeout(async () => {
      try {
        const results = await searchPlayers(trimmed);
        // Ignore stale responses: if the query has changed since we
        // fired, the newer one will deliver its own results.
        if (this.state.searchQuery.trim() !== trimmed) return;
        this.setState({ serverResults: Array.isArray(results) ? results : [], searchLoading: false });
      } catch {
        this.setState({ serverResults: [], searchLoading: false });
      }
    }, SEARCH_DEBOUNCE_MS);
  };

  handleSendRequest = async (targetId) => {
    try {
      const result = await sendFriendRequest(targetId);
      this.setState((s) => {
        const sentRequests = new Set(s.sentRequests);
        sentRequests.add(targetId);
        const autoAccepted = new Set(s.autoAccepted);
        if (result?.autoAccepted) autoAccepted.add(targetId);
        return { sentRequests, autoAccepted };
      });
    } catch (err) {
      console.warn('[ChatConversationList] sendFriendRequest failed:', err?.message);
    }
  };

  resolveRequest = async (senderId, action) => {
    if (!senderId) return;
    if (this.state.actingOn.has(senderId) || this.state.handled.has(senderId)) return;
    this.setState((s) => {
      const actingOn = new Set(s.actingOn);
      actingOn.add(senderId);
      return { actingOn };
    });
    try {
      await action(senderId);
    } catch (err) {
      // 404 means the row is already gone. The request was handled
      // via another path (auto-accept from a reciprocal "Add" click,
      // a double-tap, a different device). Treat it as success so
      // the row collapses cleanly.
      const msg = err?.message || '';
      if (!/not found/i.test(msg) && !/404/.test(msg)) {
        console.warn('[ChatConversationList] resolveRequest failed:', msg);
      }
    } finally {
      this.setState((s) => {
        const actingOn = new Set(s.actingOn);
        actingOn.delete(senderId);
        const handled = new Set(s.handled);
        handled.add(senderId);
        return { actingOn, handled };
      });
      // Pull the authoritative friend list so pendingRequests matches
      // the server. The WS event for accept is only sent to the
      // OTHER player, so the acceptor never gets it.
      refreshFriendList().catch(() => {});
    }
  };

  handleAccept = (senderId) => this.resolveRequest(senderId, acceptFriendRequest);
  handleDecline = (senderId) => this.resolveRequest(senderId, declineFriendRequest);

  buildFriendEntries(friends, conversations) {
    const convMap = new Map();
    for (const conv of conversations) convMap.set(conv.friendId, conv);

    const entries = [];

    for (const conv of conversations) {
      const friend = friends.find((f) => f.id === conv.friendId);
      const lastMsg = conv.lastMessage;
      const isSentByMe = lastMsg?.senderId !== conv.friendId;
      let preview = '';
      if (lastMsg?.type === 'draft-invite') preview = t('Draft invite');
      else if (lastMsg?.type === 'tavern') preview = t('Forwarded a Tavern message');
      else if (lastMsg?.type === 'attachment') preview = t('Sent an attachment');
      else preview = truncate(cardMentionsToPlainText(lastMsg?.body));
      if (isSentByMe && preview) preview = t('You: {preview}', { preview });

      entries.push({
        friendId: conv.friendId,
        friendName: conv.friendName || friend?.name || t('Unknown'),
        friendAvatar: conv.friendAvatar || friend?.profileAvatar,
        friendDisplayedCrownAvatarId: friend?.displayedCrownAvatarId ?? null,
        friendBackgroundCardId: friend?.backgroundCardId ?? null,
        friendIsFounder: !!friend?.isFounder,
        friendIsQaTester: !!friend?.isQaTester,
        online: friend?.online ?? false,
        presenceStatus: friend?.presenceStatus ?? null,
        customStatus: friend?.customStatus ?? null,
        lastMsg,
        preview,
        unreadCount: conv.unreadCount || 0,
        hasHistory: true,
      });
    }

    for (const f of friends) {
      if (convMap.has(f.id)) continue;
      entries.push({
        friendId: f.id,
        friendName: f.name || t('Unknown'),
        friendAvatar: f.profileAvatar,
        friendDisplayedCrownAvatarId: f.displayedCrownAvatarId ?? null,
        friendBackgroundCardId: f.backgroundCardId ?? null,
        friendIsFounder: !!f.isFounder,
        friendIsQaTester: !!f.isQaTester,
        online: f.online ?? false,
        presenceStatus: f.presenceStatus ?? null,
        customStatus: f.customStatus ?? null,
        lastMsg: null,
        preview: '',
        unreadCount: 0,
        hasHistory: false,
      });
    }

    const withHistory = entries.filter((e) => e.hasHistory);
    const withoutHistory = entries
      .filter((e) => !e.hasHistory)
      .sort((a, b) => (b.online - a.online) || (a.friendName || '').localeCompare(b.friendName || ''));
    return [...withHistory, ...withoutHistory];
  }

  render() {
    const { friendListData, sorceryCards } = this.props;
    const { conversations, loading, searchQuery, serverResults, searchLoading } = this.state;

    const friends = friendListData?.friends || [];
    const pendingRequests = friendListData?.pendingRequests || [];
    const trimmedQuery = searchQuery.trim();
    const searching = trimmedQuery.length >= MIN_QUERY_LENGTH;

    const friendEntries = this.buildFriendEntries(friends, conversations);
    const visibleFriends = searching
      ? friendEntries.filter((e) => (e.friendName || '').toLowerCase().includes(trimmedQuery.toLowerCase()))
      : friendEntries;

    // Dedupe the server-side player search against the local friends
    // list. A friend who matches the query already appears in the
    // Friends section, so we drop them from "Other Players".
    const friendIds = new Set(friends.map((f) => f.id));
    const otherPlayers = searching && Array.isArray(serverResults)
      ? serverResults.filter((r) => r?.id && r.id !== this.props.myUserId && !friendIds.has(r.id))
      : [];

    return (
      <div className="flex flex-col">
        {this.renderSearchBar(searchQuery)}

        {(() => {
          // Filter locally-handled rows so the UI doesn't flash a
          // stale Accept button after the click. The next /friends
          // refresh will replace pendingRequests with the server
          // truth and naturally clear `handled`.
          const visible = pendingRequests.filter((r) => !this.state.handled.has(r.senderId || r.id));
          return visible.length > 0 ? this.renderRequestsSection(visible, sorceryCards) : null;
        })()}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RuneSpinner size={28} />
          </div>
        ) : this.renderResults({
          visibleFriends,
          otherPlayers,
          searching,
          searchLoading,
          sorceryCards,
          hasAnyFriends: friendEntries.length > 0,
        })}
      </div>
    );
  }

  renderSearchBar(searchQuery) {
    return (
      <div className="pt-2.5 pb-2.5">
        {/* Chrome lives in .vh-social-search (index.css) so the field
            can carry hover and focus states. Inline styles cannot, and
            the field previously had neither. The surface is the carved
            SUB_PANEL recipe, so it reads as cut into the panel rather
            than laid on top of it. */}
        <div className="vh-social-search relative">
          <svg
            viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
            className="absolute left-[0.625cqw] top-1/2 -translate-y-1/2 pointer-events-none"
            style={{ width: '0.8125cqw', height: '0.8125cqw' }}
          >
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="text"
            value={searchQuery}
            placeholder={t('Search players')}
            className="w-full pl-[2cqw] pr-2.5 py-[0.5cqw] text-[0.6875cqw] outline-none"
            // Only the material comes from the sub-panel recipe. Its rim
            // and inset shadows live in .vh-social-search so the focus
            // state can transition them; an inline box-shadow would win
            // over the stylesheet and freeze the field in one state.
            style={{
              background: OPAQUE_SUB_PANEL_STYLE.background,
              backgroundBlendMode: OPAQUE_SUB_PANEL_STYLE.backgroundBlendMode,
              borderRadius: ROW_RADIUS,
            }}
            onInput={this.handleSearchInput}
          />
        </div>
      </div>
    );
  }

  renderRequestsSection(pendingRequests, sorceryCards) {
    return (
      <div className="flex flex-col gap-[0.125cqw]">
        <div className="pt-3 pb-1 flex items-center justify-between" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
          <span className="text-[0.5625cqw] font-semibold uppercase" style={{ color: ACCENT_GOLD, letterSpacing: '0.12em' }}>
            {t('Friend Requests')}
          </span>
          <span className="text-[0.5625cqw] tabular-nums" style={{ color: TEXT_MUTED }}>{pendingRequests.length}</span>
        </div>
        {pendingRequests.map((r) => {
          const senderId = r.senderId || r.id;
          const displayName = r.senderName || r.name || t('Unknown Player');
          const avatarUrl = resolveAvatarUrl(
            { profileAvatar: r.senderAvatar || r.avatarUrl || r.profileAvatar },
            sorceryCards,
          );
          const busy = this.state.actingOn.has(senderId);
          return (
            <div
              key={senderId}
              className="vh-social-row flex items-center gap-2.5 py-2"
              style={{ opacity: busy ? 0.5 : 1, paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}
            >
              {avatarUrl ? (
                <AvatarImage
                  src={avatarUrl}
                  className="rounded-full shrink-0"
                  style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, boxShadow: AVATAR_RING }}
                />
              ) : (
                <div
                  className="rounded-full flex items-center justify-center text-[0.75cqw] font-semibold shrink-0"
                  style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, background: `${GOLD} 0.14)`, boxShadow: AVATAR_RING, color: TEXT_PRIMARY }}
                >
                  {displayName[0].toUpperCase()}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="text-xs font-medium truncate" style={{ color: TEXT_PRIMARY }}>{displayName}</div>
                <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>{t('Wants to be friends')}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  className="px-2.5 py-1 text-[0.625cqw] font-semibold cursor-pointer transition-transform active:scale-[0.96]"
                  style={{ ...GOLD_BTN, borderRadius: 'var(--radius-sm)', pointerEvents: busy ? 'none' : 'auto' }}
                  disabled={busy}
                  onClick={() => this.handleAccept(senderId)}
                >
                  {t('Accept')}
                </button>
                <button
                  type="button"
                  className="px-2.5 py-1 text-[0.625cqw] cursor-pointer transition-transform active:scale-[0.96]"
                  style={{ ...BEVELED_BTN, color: TEXT_MUTED, borderRadius: 'var(--radius-sm)', pointerEvents: busy ? 'none' : 'auto' }}
                  disabled={busy}
                  onClick={() => this.handleDecline(senderId)}
                >
                  {t('Decline')}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  renderResults({ visibleFriends, otherPlayers, searching, searchLoading, sorceryCards, hasAnyFriends }) {
    if (!searching) {
      if (!hasAnyFriends) {
        return (
          <div className="text-center py-12">
            <p className="text-xs" style={{ color: TEXT_MUTED }}>{t('No friends yet')}</p>
            <p className="text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>
              {t('Search above to find and add players')}
            </p>
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-[0.125cqw]">
          {visibleFriends.map((entry, i) => this.renderFriendRow(entry, sorceryCards, i))}
        </div>
      );
    }

    const noFriendMatches = visibleFriends.length === 0;
    const noOtherMatches = otherPlayers.length === 0;
    const nothingYet = noFriendMatches && noOtherMatches && !searchLoading;

    return (
      <div className="flex flex-col">
        {visibleFriends.length > 0 ? (
          <div className="flex flex-col gap-[0.125cqw]">
            <div className="pt-3 pb-1" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
              <span className="text-[0.5625cqw] font-semibold uppercase" style={{ color: TEXT_MUTED, letterSpacing: '0.12em' }}>
                {t('Friends')}
              </span>
            </div>
            {visibleFriends.map((entry, i) => this.renderFriendRow(entry, sorceryCards, i))}
          </div>
        ) : null}

        {otherPlayers.length > 0 ? (
          <div className="flex flex-col gap-[0.125cqw]">
            <div className="pt-3 pb-1" style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}>
              <span className="text-[0.5625cqw] font-semibold uppercase" style={{ color: TEXT_MUTED, letterSpacing: '0.12em' }}>
                {t('Other Players')}
              </span>
            </div>
            {otherPlayers.map((player) => this.renderPlayerRow(player, sorceryCards))}
          </div>
        ) : null}

        {searchLoading && noFriendMatches && noOtherMatches ? (
          <div className="flex items-center justify-center py-8">
            <RuneSpinner size={24} />
          </div>
        ) : null}

        {nothingYet ? (
          <div className="text-center py-8">
            <p className="text-xs" style={{ color: TEXT_MUTED }}>{t('No players found')}</p>
            <p className="text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>{t('Try a different name')}</p>
          </div>
        ) : null}
      </div>
    );
  }

  renderFriendRow(entry, sorceryCards, index = 0) {
    const { onSelectFriend, activeFriendId } = this.props;
    const { friendId, friendName, friendAvatar, friendDisplayedCrownAvatarId, friendBackgroundCardId, friendIsFounder, friendIsQaTester, online, presenceStatus, lastMsg, preview, unreadCount } = entry;
    const isActive = activeFriendId && activeFriendId === friendId;
    const dotColor = online ? (PRESENCE_DOT[presenceStatus] || PRESENCE_DOT.online) : null;
    const avatarUrl = resolveAvatarUrl({ profileAvatar: friendAvatar }, sorceryCards);

    return (
      <button
        key={friendId}
        type="button"
        className={`group vh-social-row is-clickable vh-social-row-in flex items-center gap-2.5 py-2 cursor-pointer text-left w-full ${isActive ? 'vh-social-row-active' : ''}`}
        style={{
          paddingLeft: ROW_PADDING_X,
          paddingRight: ROW_PADDING_X,
          animationDelay: `${Math.min(index, 9) * 26}ms`,
          isolation: 'isolate',
        }}
        onClick={() => onSelectFriend({ friendId, friendName, friendAvatar, friendDisplayedCrownAvatarId, friendIsFounder, friendIsQaTester, online })}
      >
        <RowArtBackdrop
          player={{ backgroundCardId: friendBackgroundCardId, profileAvatar: friendAvatar }}
          sorceryCards={sorceryCards}
          borderRadius="var(--radius-lg)"
          shown={!!isActive}
        />
        <div className="relative shrink-0">
          {avatarUrl ? (
            <AvatarImage
              src={avatarUrl}
              className="rounded-full"
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, boxShadow: AVATAR_RING }}
            />
          ) : (
            <div
              className="rounded-full flex items-center justify-center text-[0.75cqw] font-semibold"
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, background: `${GOLD} 0.14)`, boxShadow: AVATAR_RING, color: TEXT_PRIMARY }}
            >
              {(friendName || '?')[0].toUpperCase()}
            </div>
          )}
          {dotColor && (
            <div
              className="absolute -bottom-0.5 -right-0.5 rounded-full"
              style={{ width: '0.625cqw', height: '0.625cqw', background: dotColor, boxShadow: `0 0 0 2px ${PANEL_RING}` }}
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <span
              className="text-xs font-medium truncate min-w-0"
              style={{ color: unreadCount > 0 ? TEXT_PRIMARY : TEXT_BODY }}
            >
              {friendName}{this.state.conversationIdsWithTitle.has(friendId) ? ',' : ''}
            </span>
            <span className="text-[0.625cqw] shrink-0 ml-2 tabular-nums" style={{ color: TEXT_MUTED }}>
              {timeAgo(lastMsg?.createdAt)}
            </span>
          </div>
          <PlayerTitle
            player={{ id: friendId, displayedCrownAvatarId: friendDisplayedCrownAvatarId, isFounder: !!friendIsFounder, isQaTester: !!friendIsQaTester }}
            sorceryCards={sorceryCards}
            prefix=""
            style={{
              display: 'block',
              fontSize: '0.6cqw',
              lineHeight: 1.1,
              whiteSpace: 'nowrap',
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '-0.2cqw',
              color: unreadCount > 0 ? TEXT_PRIMARY : TEXT_BODY,
            }}
            onResolve={(has) => {
              this.setState((s) => {
                const had = s.conversationIdsWithTitle.has(friendId);
                if (had === has) return null;
                const next = new Set(s.conversationIdsWithTitle);
                if (has) next.add(friendId); else next.delete(friendId);
                return { conversationIdsWithTitle: next };
              });
            }}
          />
          <div className="flex items-center justify-between mt-0.5">
            <span
              className="text-[0.6875cqw] truncate"
              style={{ color: unreadCount > 0 ? TEXT_BODY : TEXT_MUTED }}
            >
              {preview || t('Start a conversation')}
            </span>
            {unreadCount > 0 && (
              <span
                className="shrink-0 ml-2 px-1.5 py-0.5 rounded-full text-[0.5625cqw] font-bold tabular-nums"
                style={{ background: ACCENT_GOLD, color: '#0e0a06', minWidth: '1.125cqw', textAlign: 'center' }}
              >
                {unreadCount}
              </span>
            )}
          </div>
        </div>
      </button>
    );
  }

  renderPlayerRow(player, sorceryCards) {
    const { sentRequests, autoAccepted } = this.state;
    const sent = sentRequests.has(player.id);
    const added = autoAccepted.has(player.id);
    const avatarUrl = resolveAvatarUrl(player, sorceryCards);
    const displayName = player.name || t('Unknown');

    return (
      <div
        key={player.id}
        className="vh-social-row flex items-center gap-2.5 py-2"
        style={{ paddingLeft: ROW_PADDING_X, paddingRight: ROW_PADDING_X }}
      >
        <div className="relative shrink-0">
          {avatarUrl ? (
            <AvatarImage
              src={avatarUrl}
              className="rounded-full"
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, boxShadow: AVATAR_RING }}
            />
          ) : (
            <div
              className="rounded-full flex items-center justify-center text-[0.75cqw] font-semibold"
              style={{ width: AVATAR_SIZE, height: AVATAR_SIZE, background: `${GOLD} 0.08)`, boxShadow: AVATAR_RING, color: TEXT_MUTED }}
            >
              {displayName[0].toUpperCase()}
            </div>
          )}
          {player.online ? (
            <div
              className="absolute -bottom-0.5 -right-0.5 rounded-full"
              style={{ width: '0.625cqw', height: '0.625cqw', background: PRESENCE_DOT.online, boxShadow: `0 0 0 2px ${PANEL_RING}` }}
            />
          ) : null}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-xs font-medium truncate" style={{ color: TEXT_PRIMARY }}>{displayName}</div>
          <div className="text-[0.625cqw]" style={{ color: TEXT_MUTED }}>
            {player.online ? t('Online') : t('Offline')}
          </div>
        </div>

        {sent ? (
          <span
            className="text-[0.625cqw] px-2 py-0.5 rounded-md shrink-0"
            style={{ color: added ? ACCENT_GOLD : `${GOLD} 0.5)`, background: `${GOLD} 0.08)` }}
          >
            {added ? t('Added') : t('Pending')}
          </span>
        ) : (
          <button
            type="button"
            className="px-2.5 py-1 text-[0.625cqw] font-semibold cursor-pointer shrink-0 transition-transform active:scale-[0.96]"
            style={{ ...GOLD_BTN, borderRadius: 'var(--radius-sm)' }}
            onClick={() => this.handleSendRequest(player.id)}
          >
            {t('Add')}
          </button>
        )}
      </div>
    );
  }
}
